import { useEffect, useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Typography, useMediaQuery, useTheme,
} from '@mui/material';

export interface EditableCard { key: string; label: string }

interface Props {
    open: boolean;
    /** A save is still in flight: Save waits so two PUTs never overlap. */
    saving?: boolean;
    /** Every card this person's Action Center can show right now (role + permissions), in Action Center order, including those with nothing waiting now. */
    cards: EditableCard[];
    /** Keys currently hidden (may include keys of cards not on this Action Center; those are kept as they are). */
    hidden: ReadonlySet<string>;
    onClose: () => void;
    /** The complete new hidden list to save. */
    onSave: (hiddenKeys: string[]) => void;
}

/** Tick = shown. Stacks full-screen on a phone. Saving keeps hidden keys of cards that are not on this Action Center. */
export default function ActionCenterEditDialog({ open, saving = false, cards, hidden, onClose, onSave }: Props) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [shown, setShown] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (open) setShown(new Set(cards.filter(t => !hidden.has(t.key)).map(t => t.key)));
        // re-seed only when the dialog opens
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const toggle = (key: string) => setShown(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const save = () => {
        const onThisStrip = new Set(cards.map(t => t.key));
        const kept = Array.from(hidden).filter(k => !onThisStrip.has(k));
        const nowHidden = cards.filter(t => !shown.has(t.key)).map(t => t.key);
        onSave([...kept, ...nowHidden]);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={fullScreen} aria-labelledby="ac-edit-title">
            <DialogTitle id="ac-edit-title">Edit cards</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 200px' }}>
                        Tick the cards you want to see. Cards with nothing waiting right now are listed too. Only you see this choice.
                    </Typography>
                    {/* Kept out of DialogActions: on phones the help button floats over the actions' left corner. */}
                    <Button size="small" onClick={() => setShown(new Set(cards.map(t => t.key)))}>Show all</Button>
                </Box>
                <FormGroup>
                    {cards.map(t => (
                        <FormControlLabel
                            key={t.key}
                            label={t.label}
                            control={<Checkbox checked={shown.has(t.key)} onChange={() => toggle(t.key)} inputProps={{ 'data-testid': `ac-edit-${t.key}` } as React.InputHTMLAttributes<HTMLInputElement>} />}
                        />
                    ))}
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
